import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { api, isApiError, recordClientSessionStart } from "../api/client.js";
import { broadcastAuthenticatedSession, publishAuthSessionEnded, subscribeAuthSession } from "../api/authSession.js";
import type { AuthMode, OnboardingState, PublicFeatures, User } from "../api/types.js";
import type { AuthRecoveryDraft } from "./authRecoveryDraft.js";
import { AuthScreen } from "../features/auth/AuthScreen.js";
import type { AuthConfig } from "../features/auth/CompanyAuthFields.js";
import { LegalPage } from "../features/auth/LegalPage.js";
import { loadPage } from "./PageLoadBoundary.js";
import "../styles/base.css";
import "../styles/auth.css";
import "../styles/sidebar.css";
import "../styles/workspace.css";
import "../styles/chat.css";
import "../styles/composer.css";
import "../styles/sources.css";
import "../styles/reader.css";
import "../styles/skills.css";
import "../styles/settings.css";
import "../styles/notifications.css";
import "../styles/responsive.css";
import "../styles/experiment.css";
import "../styles/themes.css";

const PublicConversationSharePage = lazy(async () => ({
  default: (await loadPage(() => import("../features/sharing/PublicConversationSharePage.js"))).PublicConversationSharePage
}));
const WorkspaceShell = lazy(async () => ({ default: (await loadPage(() => import("./WorkspaceShell.js"))).WorkspaceShell }));
const OnboardingPage = lazy(async () => ({ default: (await loadPage(() => import("../features/profile/onboarding/OnboardingPage.js"))).OnboardingPage }));
const PaperDetailPage = lazy(async () => ({ default: (await loadPage(() => import("../features/works/PaperDetailPage.js"))).PaperDetailPage }));

const disabledFeatures: PublicFeatures = {
  retrievalSelector: false,
  modelSelector: false,
  profileOnboarding: false,
  globalFeedback: false,
  voiceInput: false
};

function featureProjection(value: Partial<PublicFeatures> | undefined): PublicFeatures {
  return {
    retrievalSelector: value?.retrievalSelector === true,
    modelSelector: value?.modelSelector === true,
    profileOnboarding: value?.profileOnboarding === true,
    globalFeedback: value?.globalFeedback === true,
    voiceInput: value?.voiceInput === true
  };
}

export function App() {
  if (/^\/share\/?$/.test(window.location.pathname)) {
    return (
      <Suspense fallback={<main className="boot-screen">正在打开分享内容…</main>}>
        <PublicConversationSharePage />
      </Suspense>
    );
  }
  return <Suspense fallback={<main className="boot-screen" role="status">正在打开工作台…</main>}><AuthenticatedApp /></Suspense>;
}

function AuthenticatedApp() {
  const legalMatch = window.location.pathname.match(/^\/legal\/(terms|privacy)(?:\/(v1\.0))?\/?$/);
  const [booting, setBooting] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [features, setFeatures] = useState<PublicFeatures>(disabledFeatures);
  const [authMode, setAuthMode] = useState<AuthMode>("local");
  const authConfigRequest = useRef<Promise<AuthConfig> | null>(null);
  const userRef = useRef<User | null>(null);
  const [bootError, setBootError] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [authWarning, setAuthWarning] = useState("");
  const [reauthRequired, setReauthRequired] = useState(false);
  const [bootReauthPending, setBootReauthPending] = useState(false);
  const [bootReauthError, setBootReauthError] = useState("");
  const [reauthDraftToRestore, setReauthDraftToRestore] = useState<AuthRecoveryDraft | null>(null);
  const reauthDraftRef = useRef<AuthRecoveryDraft | null>(null);
  const reauthDraftReadyRef = useRef(false);
  const lastAuthCheckAt = useRef(0);
  const authGeneration = useRef(0);
  const bootRequest = useRef<{ generation: number; controller: AbortController } | null>(null);
  const authCheckInFlight = useRef<{
    generation: number;
    controller: AbortController;
    promise: Promise<void>;
  } | null>(null);
  const revalidateAuth = useRef<() => void>(() => undefined);
  const [onboarding, setOnboarding] = useState<OnboardingState | null>(null);
  // onboarding 视图是否挂载由 boot 时一次性决定,之后只被 onSkipped/onCompleted 关闭;
  // 状态流式变成 completed 时不自动切走,让用户读完最终总结再进工作台。
  const [onboardingActive, setOnboardingActive] = useState(false);

  useEffect(() => {
    if (features.profileOnboarding) return;
    setOnboarding(null);
    setOnboardingActive(false);
  }, [features.profileOnboarding]);

  const updateUser = (nextUser: User | null) => {
    userRef.current = nextUser;
    setUser(nextUser);
  };

  const loadOnboarding = async (options: { signal?: AbortSignal; suppressAuthSessionEnd?: boolean } = {}) => {
    const data = await api<{ onboarding: OnboardingState }>("/api/profile/onboarding", options);
    return data.onboarding;
  };

  const refreshOnboarding = async () => {
    const onboardingState = await loadOnboarding();
    setOnboarding(onboardingState);
    return onboardingState;
  };

  const invalidateAuthRequests = () => {
    const generation = authGeneration.current + 1;
    authGeneration.current = generation;
    bootRequest.current?.controller.abort();
    bootRequest.current = null;
    authCheckInFlight.current?.controller.abort();
    authCheckInFlight.current = null;
    return generation;
  };

  const boot = async () => {
    // Public configuration is independent of session verification. Share this
    // request with the eventual login form, without delaying authenticated users.
    if (!authConfigRequest.current && /^\/login\/?$/.test(window.location.pathname)) {
      const request = api<AuthConfig>("/api/auth/config");
      authConfigRequest.current = request;
      void request.catch(() => {
        if (authConfigRequest.current === request) authConfigRequest.current = null;
      });
    }
    const hadAuthenticatedUser = Boolean(userRef.current);
    let verifiedUser = false;
    const generation = invalidateAuthRequests();
    const controller = new AbortController();
    bootRequest.current = { generation, controller };
    setBooting(true);
    setBootError("");
    try {
      const data = await api<{ user: User; features?: PublicFeatures; authMode?: AuthMode }>("/api/auth/me", {
        signal: controller.signal,
        suppressAuthSessionEnd: true
      });
      if (authGeneration.current !== generation) return false;
      updateUser(data.user);
      setReauthRequired(false);
      setAuthWarning("");
      setBootReauthError("");
      setAuthMode(data.authMode === "company" ? "company" : "local");
      const nextFeatures = featureProjection(data.features);
      setFeatures(nextFeatures);
      verifiedUser = true;
      lastAuthCheckAt.current = Date.now();
      setAuthWarning("");
      void recordClientSessionStart(controller.signal);
      if (nextFeatures.profileOnboarding) {
        const onboardingState = await loadOnboarding({
          signal: controller.signal,
          suppressAuthSessionEnd: true
        });
        if (authGeneration.current !== generation) return false;
        setOnboarding(onboardingState);
        setOnboardingActive(onboardingState.status === "pending");
      } else {
        setOnboarding(null);
        setOnboardingActive(false);
      }
      // Do not consume the in-memory draft until the full authenticated boot
      // succeeds. A later boot retry can still restore it if onboarding fails.
      if (reauthDraftReadyRef.current) {
        const draft = reauthDraftRef.current;
        reauthDraftReadyRef.current = false;
        if (draft?.userId === data.user.id) {
          setReauthDraftToRestore(draft);
        } else {
          reauthDraftRef.current = null;
          setReauthDraftToRestore(null);
        }
      }
      return true;
    } catch (error) {
      if (authGeneration.current !== generation || (error instanceof Error && error.name === "AbortError")) return false;
      if (isApiError(error) && error.status === 401 && (hadAuthenticatedUser || verifiedUser)) {
        publishAuthSessionEnded("expired");
        return false;
      }
      updateUser(null);
      setFeatures(disabledFeatures);
      setOnboarding(null);
      setOnboardingActive(false);
      if (!isApiError(error) || error.status !== 401) {
        const needsReauth = isApiError(error) && error.code === "company_session_reauth_required";
        setReauthRequired(needsReauth);
        setBootError(needsReauth
          ? "公司登录状态需要重新验证，请重新登录后继续。"
          : isApiError(error) && error.code === "company_auth_unavailable"
            ? "公司认证服务暂时不可用，请稍后重试。"
            : "暂时无法确认登录状态，请检查网络后重试。");
      }
      return false;
    } finally {
      if (bootRequest.current?.generation === generation) {
        bootRequest.current = null;
        setBooting(false);
      }
    }
  };

  const endSession = (reason: "expired" | "logout", remote: boolean) => {
    const hadUser = Boolean(userRef.current);
    invalidateAuthRequests();
    setBooting(false);
    setBootError("");
    setAuthWarning("");
    setReauthRequired(false);
    setBootReauthError("");
    if (!hadUser) return;
    authConfigRequest.current = null;
    updateUser(null);
    setFeatures(disabledFeatures);
    setOnboarding(null);
    setOnboardingActive(false);
    setAuthWarning("");
    if (!/^\/login\/?$/.test(window.location.pathname)) {
      window.history.replaceState(null, "", "/login");
    }
    setAuthNotice(reason === "expired"
      ? "登录状态已过期，请重新登录。"
      : remote ? "账号已在其他页面退出，请重新登录。" : "");
  };

  useEffect(() => subscribeAuthSession((event, remote) => {
    if (event.type === "reauth-required") {
      setReauthRequired(true);
      setAuthWarning("公司登录状态需要重新验证，请重新登录后继续使用。");
    } else if (event.type === "ended") {
      endSession(event.reason, remote);
    } else if (event.type === "authenticated") {
      void boot();
    }
  }), []);

  useEffect(() => { void boot(); }, []);

  useEffect(() => {
    if (!user) return;
    const revalidate = () => {
      const generation = authGeneration.current;
      const activeCheck = authCheckInFlight.current;
      if (activeCheck?.generation === generation) return activeCheck.promise;
      activeCheck?.controller.abort();
      const controller = new AbortController();
      const check = api<{ user: User; features?: PublicFeatures; authMode?: AuthMode }>("/api/auth/me", {
        signal: controller.signal,
        suppressAuthSessionEnd: true
      })
        .then((data) => {
          if (authGeneration.current !== generation) return;
          updateUser(data.user);
          void recordClientSessionStart(controller.signal);
          setAuthMode(data.authMode === "company" ? "company" : "local");
          setFeatures(featureProjection(data.features));
          lastAuthCheckAt.current = Date.now();
          setAuthWarning("");
          setReauthRequired(false);
        })
        .catch((error) => {
          if (authGeneration.current !== generation || (error instanceof Error && error.name === "AbortError")) return;
          if (isApiError(error) && error.status === 401) {
            publishAuthSessionEnded("expired");
            return;
          }
          if (isApiError(error) && error.code === "company_session_reauth_required") {
            setReauthRequired(true);
            setAuthWarning("公司登录状态需要重新验证，请重新登录后继续使用。");
          } else {
            setAuthWarning(isApiError(error) && error.code === "company_auth_unavailable"
              ? "公司认证服务暂时不可用，当前操作可能失败。"
              : "网络连接异常，暂时无法确认登录状态。");
          }
        })
        .finally(() => {
          if (authCheckInFlight.current?.controller === controller) authCheckInFlight.current = null;
        });
      authCheckInFlight.current = { generation, controller, promise: check };
      return check;
    };
    revalidateAuth.current = () => {
      void revalidate();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && Date.now() - lastAuthCheckAt.current >= 5 * 60_000) void revalidate();
    };
    const onFocus = () => {
      if (document.visibilityState === "visible" && Date.now() - lastAuthCheckAt.current >= 5 * 60_000) void revalidate();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    return () => {
      revalidateAuth.current = () => undefined;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, [user?.id]);

  const authenticated = async () => {
    setAuthNotice("");
    if (await boot()) {
      if (/^\/login\/?$/.test(window.location.pathname)) {
        window.history.replaceState(null, "", "/app");
      }
      broadcastAuthenticatedSession();
    }
  };

  const logout = () => {
    reauthDraftRef.current = null;
    reauthDraftReadyRef.current = false;
    setReauthDraftToRestore(null);
    publishAuthSessionEnded("logout");
  };

  const logoutForReauth = () => {
    reauthDraftReadyRef.current = Boolean(reauthDraftRef.current);
    publishAuthSessionEnded("logout");
  };

  const captureReauthDraft = (draft: AuthRecoveryDraft) => {
    reauthDraftRef.current = draft;
    reauthDraftReadyRef.current = false;
    setReauthDraftToRestore(null);
  };

  const clearRestoredReauthDraft = useCallback((draft: AuthRecoveryDraft) => {
    if (reauthDraftRef.current !== draft) return;
    reauthDraftRef.current = null;
    reauthDraftReadyRef.current = false;
    setReauthDraftToRestore(null);
  }, []);

  const reauthenticateFromBootError = async () => {
    if (bootReauthPending) return;
    setBootReauthPending(true);
    setBootReauthError("");
    try {
      await api("/api/auth/logout", { method: "POST", body: JSON.stringify({}) });
      if (!/^\/login\/?$/.test(window.location.pathname)) {
        window.history.replaceState(null, "", "/login");
      }
      publishAuthSessionEnded("logout");
    } catch {
      setBootReauthError("无法结束当前登录状态，请检查网络后重试。");
    } finally {
      setBootReauthPending(false);
    }
  };

  const leaveOnboarding = () => {
    setOnboardingActive(false);
    void refreshOnboarding();
  };

  if (legalMatch) {
    return <LegalPage
      kind={legalMatch[1] as "terms" | "privacy"}
      version={legalMatch[2] as "v1.0" | undefined}
    />;
  }
  if (booting) return <main className="boot-screen">Truth Seeker</main>;
  if (bootError) {
    return (
      <main className="auth-shell">
        <section className="auth-panel" role="alert">
          <h1>{reauthRequired ? "登录状态需要重新验证" : "暂时无法连接认证服务"}</h1>
          <p className="auth-error">{bootError}</p>
          {reauthRequired && (
            <button className="primary-action" type="button" onClick={() => void reauthenticateFromBootError()} disabled={bootReauthPending}>
              {bootReauthPending ? "处理中…" : "重新登录"}
            </button>
          )}
          <button
            className={!reauthRequired ? "primary-action" : undefined}
            type="button"
            onClick={() => void boot()}
          >重新检查</button>
          {bootReauthError && <p className="auth-error" role="alert">{bootReauthError}</p>}
        </section>
      </main>
    );
  }
  if (!user) return <AuthScreen onAuthed={() => void authenticated()} sessionNotice={authNotice} configRequest={authConfigRequest.current || undefined} />;
  if (onboardingActive && onboarding) {
    return (
      <OnboardingPage
        initialState={onboarding}
        onStateChange={setOnboarding}
        onCompleted={leaveOnboarding}
        onSkipped={leaveOnboarding}
      />
    );
  }
  const paperMatch = window.location.pathname.match(/^\/papers\/([^/]+)\/?$/);
  if (paperMatch) {
    const sessionId = new URLSearchParams(window.location.search).get("sessionId");
    return <PaperDetailPage workId={decodeURIComponent(paperMatch[1])} sessionId={sessionId} />;
  }
  return (
    <WorkspaceShell
      key={user.id}
      initialUser={user}
      authMode={authMode}
      features={features}
      onLogout={logout}
      onReauthLogout={logoutForReauth}
      onReauthDraftCaptured={captureReauthDraft}
      reauthDraft={reauthDraftToRestore?.userId === user.id ? reauthDraftToRestore : null}
      onReauthDraftRestored={clearRestoredReauthDraft}
      authWarning={authWarning}
      reauthRequired={reauthRequired}
      onRetryAuth={() => revalidateAuth.current()}
      onUserUpdated={updateUser}
    />
  );
}
